import { createContext, useContext } from 'react'
import { newTile, OverviewTile } from './defaults'
import OverviewTileType, {
  overviewTileTypes,
} from 'app/frontend/types/custom/OverviewTileType'
import { Section } from 'app/frontend/types/custom'

export interface OverviewContextType {
  isCustomizing: boolean
  isPending: boolean
  onEditTile: (tile: OverviewTile, index: number) => void
}

export const OverviewContext = createContext<OverviewContextType>({
  isCustomizing: false,
  isPending: false,
  onEditTile: () => undefined,
})

export const useOverviewEditorState = () => {
  const context = useContext(OverviewContext)
  if (context === undefined) {
    throw new Error('useOverviewState must be used within a OverviewProvider')
  }

  return context
}

export interface TileContextType {
  tile: OverviewTile
  index: number
}

export const TileContext = createContext<TileContextType>({
  tile: newTile,
  index: 0,
})

export const useTile = () => {
  const context = useContext(TileContext)
  if (context === undefined) {
    throw new Error('useTile must be used within a TileProvider')
  }

  return context
}

export interface OverviewConfigContextType {
  allowedTileTypes: readonly OverviewTileType[]
  defaultTiles: OverviewTile[]
}

export const OverviewConfigContext = createContext<OverviewConfigContextType>({
  allowedTileTypes: overviewTileTypes,
  defaultTiles: [],
})

export const useOverviewConfig = () => {
  const context = useContext(OverviewConfigContext)
  if (context === undefined) {
    throw new Error('useOverviewConfig must be used within a OverviewProvider')
  }

  return context
}

export interface OverviewRuntimeContextType {
  overviewData?: wowaudit.OverviewData
  isOverviewDataLoading: boolean
  ownerPath: string
  ownerName: string
  guildMotd?: string
  teamReference?: wowaudit.TeamReference
  requireAllTeams?: boolean
  uploadFile: (file: File) => Promise<string>
  recentNewsEditPermission: {
    section: Section
    requirement: 'admin'
    requireAllTeams?: boolean
  }
}

export const OverviewRuntimeContext = createContext<OverviewRuntimeContextType>(
  {} as OverviewRuntimeContextType,
)

export const useOverviewRuntime = () => {
  const context = useContext(OverviewRuntimeContext)
  if (context === undefined) {
    throw new Error('useOverviewRuntime must be used within a OverviewProvider')
  }

  return context
}
